import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateProject, useUpdateProject, useProjectById, useCreateFromTemplate, useNextProjectNumber, useNextProjectNumberPreview, useProjectCategoryOptions } from '@/hooks';
import { projectCreateSchema, type ProjectCreate, type Project } from '@/lib/schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { WorkflowStageSelect, type WorkflowStage } from '@/components/forms/WorkflowStageSelect';
import { SearchableOrganizationCombobox } from '@/components/organizations/SearchableOrganizationCombobox';
import { FeeAssumptionsInput } from '@/components/FeeAssumptionsInput';
import { OrganizationFormModal } from '@/components/forms/OrganizationFormModal';
import { ClientContactSelect } from '@/components/forms/ClientContactSelect'; // Phase 7.5.5
import { CategoryChangeWarning } from '@/components/CategoryChangeWarning'; // EDIT-02: Category change warning

// BUG-01 FIX: Proposal stages that should have NULL project_number
const PROPOSAL_STAGES = ['proposal_draft', 'proposal_sent', 'proposal_declined'];

interface ProjectFormProps {
  project?: Project;
  mode: 'create' | 'edit';
  isTemplate?: boolean; // Phase 7 Batch 7.3: Template support
  onSuccess?: () => void; // EDIT-01: Optional callback instead of navigation
}

export function ProjectForm({ project, mode, isTemplate = false, onSuccess }: ProjectFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const createFromTemplate = useCreateFromTemplate();

  // Fetch next project number from backend (always accurate)
  const { data: nextNumberData } = useNextProjectNumber();

  // Phase 7.3: Infer isTemplate from project data in edit mode for safety
  const effectiveIsTemplate = mode === 'edit' && project ? project.is_template : isTemplate;

  // Check if creating from template (fromTemplate parameter in URL)
  // Phase 7.5.13: Templates always use UUID, not project_number
  const templateParam = searchParams.get('fromTemplate');

  // Use UUID-based lookup for templates
  const { data: templateData } = useProjectById(templateParam || undefined);
  const template = templateData?.project;
  const templateId = templateParam;
  const { data: projectCategoryOptions, isLoading: isLoadingCategories } = useProjectCategoryOptions();

  // State for "Add New Organization" modal
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);

  // Phase 7.5.5: State for client contact
  const [clientContactId, setClientContactId] = useState<string | null>(null);

  // EDIT-02: State for category change warning
  const [showCategoryWarning, setShowCategoryWarning] = useState(false);
  const [pendingCategory, setPendingCategory] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    clearErrors,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ProjectCreate>({
    resolver: zodResolver(projectCreateSchema),
    defaultValues: project
      ? {
          project_number: project.project_number,
          name: project.name,
          organization_id: project.organization_id || undefined,
          address: project.address,
          po_number: project.po_number || '', // SPEC-project-po-number-2026-08-17 R1
          // Phase 9.5f: status field removed - workflow_stage is now the sole state indicator
          notes: project.notes,
          fee_assumptions: project.fee_assumptions || '', // Phase 7.3.3: Fee assumptions
          project_understanding: project.project_understanding || '', // Phase 8.3: Project understanding
          is_template: project.is_template || false, // Phase 7.3: Include is_template for edit mode
          category: project.category || null,
        }
      : {
          // Phase 7.5.13: Templates have null project_number, regular projects get empty string (will be updated from API)
          // Phase 9.6a: project_number is now TEXT for prefix support - use empty string, not 0
          project_number: effectiveIsTemplate ? null : '',
          po_number: '', // SPEC-project-po-number-2026-08-17 R1: default empty PO
          // Phase 9.5f: status field removed - workflow_stage is now the sole state indicator
          fee_assumptions: '', // Phase 7.3.3: Default empty fee assumptions
          project_understanding: '', // Phase 8.3: Default empty project understanding
          is_template: effectiveIsTemplate, // Phase 7.3: Set template flag from prop/project
          category: null,
        },
  });

  // BUG-01 FIX: Watch workflow_stage to hide project_number for proposals
  const watchedWorkflowStage = watch('workflow_stage');
  const isProposalStage = PROPOSAL_STAGES.includes(watchedWorkflowStage || '');
  // 2026-09-13 template-category fix: create-from-template ALWAYS creates a proposal_draft
  // project with a NULL project_number (backend hardcodes this — the real, category-prefixed
  // number is assigned later, at proposal-to-project conversion). Whatever this form showed
  // or sent for project_number was silently discarded, so treat this flow like every other
  // other proposal-stage creation and hide the field rather than show a number that will
  // never be stored.
  const shouldHideProjectNumber = effectiveIsTemplate || isProposalStage || !!templateId;

  // Phase 9.6b: Watch category to get category-aware project number preview
  const selectedCategory = watch('category') ?? null;
  const selectedCategoryOption = projectCategoryOptions?.find(c => c.value === selectedCategory);
  const selectedCategoryId = selectedCategoryOption?.id;

  // Phase 9.6b: Get category-aware project number preview
  const { data: categoryPreviewNumber } = useNextProjectNumberPreview(selectedCategoryId);

  // Auto-suggest next project number from backend
  // Phase 9.6b: Use category-based preview when available, fallback to legacy
  useEffect(() => {
    if (mode === 'create' && !shouldHideProjectNumber) {
      if (categoryPreviewNumber) {
        // Category-based prefixed number (e.g., "A-47")
        setValue('project_number', String(categoryPreviewNumber));
      } else if (nextNumberData?.next_number && !selectedCategory) {
        // Legacy fallback when no category selected
        setValue('project_number', String(nextNumberData.next_number));
      }
    }
  }, [mode, categoryPreviewNumber, nextNumberData, selectedCategory, shouldHideProjectNumber, setValue]);

  // Load template data into form when template is fetched (WITHOUT resetting project_number)
  useEffect(() => {
    if (template && mode === 'create') {
      // Only set specific fields, DON'T reset project_number (keep suggestion)
      setValue('name', template.name);
      setValue('address', template.address || '');
      setValue('notes', template.notes || '');
      setValue('fee_assumptions', template.fee_assumptions || ''); // Phase 7.3.3: Copy fee assumptions from template
      setValue('project_understanding', template.project_understanding || ''); // Phase 8.3: Copy project understanding from template
      // 2026-09-13 template-category fix: the category picker previously stayed at its
      // "No category" default here — whatever the user picked was never sent to the backend
      // anyway (create_from_template unconditionally copied the TEMPLATE's category), so the
      // picker showed the wrong value AND did nothing. Pre-fill it with the template's own
      // category (the value the new project will actually get by default), while still
      // letting the user change it — the payload below now sends whatever this resolves to.
      setValue('category', template.category ?? null, { shouldDirty: false });
      // organization_id stays undefined - user must select client
    }
  }, [template, mode, setValue]);

  const handleCategoryChange = (value: string) => {
    // 2026-09-13 template-category fix: Radix Select's hidden native <select> (kept in sync
    // for browser autofill / native form semantics) can fire a spurious onValueChange('')
    // when the CONTROLLED value is changed programmatically (via setValue, e.g. this form's
    // template-category pre-fill effect) rather than through a real click on a SelectItem —
    // the native <select> briefly has no matching <option> for the new value, so its value
    // reads back as "". "" is never a real option here (no-category is "__none__"), so this
    // is unambiguously the spurious case, not a user choosing something — ignore it rather
    // than let it silently blank out a category the effect just set.
    if (value === '') {
      return;
    }
    const newCategory = value === '__none__' ? null : value;

    // EDIT-02: Show warning if changing category on a numbered project in edit mode
    // Warning should show when:
    // 1. In edit mode
    // 2. Project has an assigned number
    // 3. Category is actually changing
    if (
      mode === 'edit' &&
      project?.project_number &&
      project?.category !== newCategory
    ) {
      setPendingCategory(newCategory);
      setShowCategoryWarning(true);
    } else {
      // No warning needed - set category directly
      setValue('category', newCategory, { shouldDirty: true });
    }
  };

  // EDIT-02: Confirm category change after warning
  const confirmCategoryChange = () => {
    setValue('category', pendingCategory, { shouldDirty: true });
    setShowCategoryWarning(false);
    setPendingCategory(null);
  };

  // EDIT-02: Cancel category change - reset pending state
  const cancelCategoryChange = () => {
    setShowCategoryWarning(false);
    setPendingCategory(null);
  };

  const onSubmit = async (data: ProjectCreate) => {
    let navigationPath: string | null = null;

    try {
      if (mode === 'create') {
        // Phase 7.3.3: Check if creating from template
        if (templateId) {
          // Creating from template - requires organization_id
          if (!data.organization_id) {
            // Set form error to make it visible to user
            setError('organization_id', {
              type: 'manual',
              message: 'Client is required when creating from template',
            });
            console.error('❌ Organization ID is required when creating from template');
            return;
          }

          const mutationPayload = {
            templateId,
            data: {
              organization_id: data.organization_id,
              // 2026-09-13 template-category fix: project_number is NOT sent here anymore —
              // create_from_template always creates a proposal_draft with NULL
              // project_number (the real, category-prefixed number is assigned later at
              // conversion), so the field is hidden above and sending a value for it would
              // just be a number nobody asked for and nothing will use.
              name: data.name, // User can customize
              address: data.address || undefined,
              notes: data.notes || undefined,
              client_contact_id: clientContactId || undefined, // Phase 7.5.5
              // 2026-09-13 template-category fix: send the category shown on the form
              // (pre-filled from the template, above) so it's what the new project actually
              // gets — previously whatever was picked here was silently dropped and the
              // template's category was copied instead, regardless of this control.
              category: data.category ?? null,
            },
          };

          const result = await createFromTemplate.mutateAsync(mutationPayload);
          navigationPath = `/projects/${result.project.id}`;
        } else {
          // Phase 7 Batch 7.3: Include is_template flag
          // Migration V012: Templates must not have organization_id
          // Phase 7.5.13: Templates must not have project_number
          // BUG-01 FIX: Proposals must also have NULL project_number
          const isProposal = PROPOSAL_STAGES.includes(data.workflow_stage || '');
          const shouldNullifyProjectNumber = isTemplate || isProposal;

          const result = await createProject.mutateAsync({
            ...data,
            is_template: isTemplate,
            organization_id: isTemplate ? null : data.organization_id,
            project_number: shouldNullifyProjectNumber ? null : data.project_number,
            client_contact_id: isTemplate ? null : (clientContactId || undefined), // Phase 7.5.5
          });

          // Navigate based on whether this is a template or project
          if (isTemplate) {
            navigationPath = '/settings?tab=templates';
          } else {
            navigationPath = `/projects/${result.project.id}`;
          }
        }
      } else if (project) {
        await updateProject.mutateAsync({ id: project.id, data });
        navigationPath = `/projects/${project.id}`;
      }
    } catch (error: any) {
      // Mutation hooks (useCreateProject, useUpdateProject) handle error toasts via onError
      // CRITICAL: Don't navigate on error - stay on form to show validation errors
      return;
    }

    // Navigate after successful create/update. Don't invalidate ['projects'] here —
    // useCreateProject.onSuccess already set the cache via setQueryData.
    // A broad invalidateQueries would trigger a refetch that overwrites it with stale data.
    if (navigationPath) {

      // EDIT-01: If onSuccess callback provided, call it instead of navigating
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(navigationPath);
      }
    }
  };

  const selectedOrganizationId = watch('organization_id');

  // Phase 7.5.5: Clear client contact when organization changes
  useEffect(() => {
    // Clear contact when organization changes
    setClientContactId(null);
  }, [selectedOrganizationId]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            {templateId
              ? `Create Project from Template: ${template?.name || 'Loading...'}`
              : effectiveIsTemplate
                ? 'Template Details'
                : 'Project Details'}
          </CardTitle>
          <CardDescription>
            {templateId
              ? 'Customize the project details below. All billable items and scopes will be copied from the template.'
              : mode === 'create'
                ? effectiveIsTemplate
                  ? 'Create a new template for reusable project structures'
                  : 'Create a new project to track billable work'
                : 'Update project information'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Project Number - Hidden for templates AND proposals (BUG-01 FIX: proposals have NULL project_number) */}
          {/* Phase 9.6a: Changed from type="number" to type="text" to support prefixes like "CHR-1047" */}
          {!shouldHideProjectNumber && (
            <div className="space-y-2">
              <Label htmlFor="project_number">
                Project Number <span className="text-destructive">*</span>
              </Label>
              <Input
                id="project_number"
                type="text"
                placeholder="e.g., A-47 or 1047"
                {...register('project_number')}
                className={errors.project_number ? 'border-destructive' : ''}
              />
              {mode === 'create' && (
                <p className="text-xs text-muted-foreground">
                  Auto-generated based on category. You can customize if needed.
                </p>
              )}
              {errors.project_number && (
                <p className="text-sm text-destructive">
                  {errors.project_number.message}
                </p>
              )}
            </div>
          )}

          {/* PO Number - SPEC-project-po-number-2026-08-17 R1. Always visible
              (unlike Project Number above, which hides for proposals/templates) --
              a client PO can arrive before a project number is even assigned. */}
          <div className="space-y-2">
            <Label htmlFor="po_number">PO Number</Label>
            <Input
              id="po_number"
              type="text"
              placeholder="e.g., PO-2026-118 (optional)"
              {...register('po_number')}
              className={errors.po_number ? 'border-destructive' : ''}
            />
            <p className="text-xs text-muted-foreground">
              The client&apos;s own purchase order reference (their accounts-payable
              matching key) — not this project's own project number.
            </p>
            {errors.po_number && (
              <p className="text-sm text-destructive">{errors.po_number.message}</p>
            )}
          </div>

          {/* Project Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Project Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="e.g. Residential Addition"
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          {/* Client - Required for projects, must be empty for templates (Migration V012) */}
          <div className="space-y-2">
            <Label htmlFor="organization_id">
              {effectiveIsTemplate ? 'Client' : 'Client *'}
            </Label>
            {effectiveIsTemplate ? (
              <div className="text-sm text-muted-foreground border rounded-md px-3 py-2 bg-muted">
                Templates are tenant-wide and not associated with a specific client
              </div>
            ) : (
              <SearchableOrganizationCombobox
                value={selectedOrganizationId || undefined}
                onValueChange={(value) => {
                  setValue('organization_id', value);
                  // Clear validation error when org is selected
                  if (value) {
                    clearErrors('organization_id');
                  }
                }}
                role="client"
                useServerSearch={true}
                placeholder="Select a client"
                className={errors.organization_id ? 'border-destructive' : ''}
                onCreateNew={() => setShowCreateOrgModal(true)}
              />
            )}
          {!effectiveIsTemplate && errors.organization_id && (
            <p className="text-sm text-destructive">{errors.organization_id.message}</p>
          )}
        </div>

        {/* Phase 7.5.5: Client Contact - Only for projects, not templates */}
        {!effectiveIsTemplate && (
          <ClientContactSelect
            organizationId={selectedOrganizationId}
            value={clientContactId}
            onValueChange={(value) => setClientContactId(value)}
            autoSelectPrimary={true}
          />
        )}

        {/* Project Category */}
        <div className="space-y-2">
          <Label htmlFor="project-category">Project Category (optional)</Label>
          <Select
            value={selectedCategory ?? '__none__'}
            onValueChange={handleCategoryChange}
            disabled={isLoadingCategories || (projectCategoryOptions?.length ?? 0) === 0}
          >
            <SelectTrigger id="project-category">
              <SelectValue placeholder={isLoadingCategories ? 'Loading categories...' : 'Select category'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No category</SelectItem>
              {(projectCategoryOptions ?? []).map((category) => (
                <SelectItem key={category.id} value={category.value}>
                  {category.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Used to tag and filter projects.
          </p>
        </div>

          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              {...register('address')}
              placeholder="Project address (optional)"
            />
          </div>

          {/* Phase 9.5f: Status field completely removed - workflow_stage is now the sole state indicator */}

          {/* Workflow Stage */}
          <div className="space-y-2" id="workflow_stage">
            <Label htmlFor="workflow_stage">Workflow Stage</Label>
            <WorkflowStageSelect
              value={watch('workflow_stage') as WorkflowStage | undefined}
              onChange={(value) => setValue('workflow_stage', value)}
              disabled={isSubmitting}
            />
            {errors.workflow_stage && (
              <p className="text-sm text-destructive">{errors.workflow_stage.message}</p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <textarea
              id="notes"
              {...register('notes')}
              rows={4}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Any additional notes about the project (optional)"
            />
          </div>

          {/* Project Understanding - Phase 8.3: For proposal PDFs */}
          <div className="space-y-2">
            <Label htmlFor="project_understanding">Project Understanding</Label>
            <textarea
              id="project_understanding"
              {...register('project_understanding')}
              rows={6}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Describe your understanding of the project scope and context. This appears in proposal PDFs. Use separate paragraphs for different topics."
            />
            <p className="text-xs text-muted-foreground">
              This text appears in the &quot;Project Understanding&quot; section of proposal PDFs. Each paragraph (separated by blank lines) will be formatted separately.
            </p>
          </div>

          {/* Fee Assumptions - Phase 7.3.3 */}
          <FeeAssumptionsInput
            value={watch('fee_assumptions') || ''}
            onChange={(value) => setValue('fee_assumptions', value)}
            disabled={isSubmitting}
          />
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex items-center gap-4">
        <Button
          type="submit"
          disabled={
            isSubmitting ||
            (mode === 'create' && !effectiveIsTemplate && !nextNumberData?.next_number)
          }
        >
          {isSubmitting
            ? mode === 'create'
              ? 'Creating...'
              : 'Saving...'
            : mode === 'create'
              ? templateId
                ? 'Create Project from Template'
                : effectiveIsTemplate
                  ? 'Create Template'
                  : 'Create Project'
              : 'Save Changes'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/projects')}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>

      {/* Add New Organization Modal */}
      <OrganizationFormModal
        open={showCreateOrgModal}
        onOpenChange={setShowCreateOrgModal}
        fieldSet="minimal"
        onSave={(organization) => {
          // Auto-select the newly created organization
          setValue('organization_id', organization.id);
          // Clear validation error since org is now selected
          clearErrors('organization_id');
        }}
      />

      {/* EDIT-02: Category Change Warning Dialog */}
      <CategoryChangeWarning
        open={showCategoryWarning}
        onOpenChange={cancelCategoryChange}
        currentCategory={project?.category || ''}
        newCategory={pendingCategory || ''}
        projectNumber={project?.project_number || ''}
        onConfirm={confirmCategoryChange}
      />
    </form>
  );
}
